import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ValidateStep.css';

interface ValidateStepProps {
  cvrt: string;
  phoneLastDigits: string | null;
  loading: boolean;
  error: string | null;
  errorStatus: number | null;
  onValidate: (cvrt: string) => Promise<void>;
  onNext: () => void;
}

const ValidateStep: React.FC<ValidateStepProps> = ({
  cvrt,
  phoneLastDigits,
  loading,
  error,
  errorStatus,
  onValidate,
  onNext,
}) => {
  const { t } = useTranslation();
  const hasTriedValidation = useRef(false);

  useEffect(() => {
    if (cvrt && !phoneLastDigits && !hasTriedValidation.current) {
      hasTriedValidation.current = true;
      onValidate(cvrt);
    }
  }, [cvrt, phoneLastDigits, onValidate]);

  // Reset the flag when CVRT changes
  useEffect(() => {
    hasTriedValidation.current = false;
  }, [cvrt]);

  return (
    <div className="validate-step">
      <div className="card">
        <div className="card-header">
          <h2>{t('validate.title')}</h2>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>{t('validate.description')}</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="alert alert-error">
                <strong>{t('common.error')}</strong>
                <p>{error}</p>
              </div>
              {errorStatus !== 401 && (
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    hasTriedValidation.current = false;
                    onValidate(cvrt);
                  }}
                >
                  {t('common.retry')}
                </button>
              )}
              {errorStatus === 401 && (
                <p style={{ 
                  marginTop: '1rem', 
                  fontSize: '0.9rem', 
                  opacity: 0.8, 
                  textAlign: 'center' 
                }}>
                  Please contact support or check your request token.
                </p>
              )}
            </div>
          ) : phoneLastDigits ? (
            <div className="success-container">
              <div className="alert alert-success">
                <div className="phone-info">
                  <span className="phone-icon">📱</span>
                  <p>{t('validate.phoneEndsWith', { digits: phoneLastDigits })}</p>
                </div>
              </div>
              <button 
                className="btn btn-primary btn-large"
                onClick={onNext}
              >
                {t('validate.continue')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ValidateStep;